import { logger } from '@vagabondinternet/logging';
import {
  type BoardGameMoveOutcome,
  type BoardGamePlacement,
  type BoardGameView,
  refusalMessage,
} from '@words-happen/screens';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type BoardGameApi, type BoardGameMoveResult, boardGameFailure } from '../api/games';
import { useSession } from '../session/session-context';
import { documentVisibility, type PageVisibility } from './page-visibility';

/** How often the game is fetched while the opponent is to move and the tab is visible. */
export const BOARD_GAME_POLL_MS = 5_000;

/** What the player reads when the game had moved on before their move arrived. */
export const MOVED_ON_MESSAGE = "The game had moved on, so that move wasn't played. Here's where it stands now.";
/** What the player reads when a move couldn't be sent as it was. */
export const MALFORMED_MESSAGE = "That move couldn't be sent as it was. Your tiles are back on your rack.";
/** What the player reads when a move didn't reach the server, or the server failed. */
export const MOVE_FAILED_MESSAGE = "That didn't go through. Check your connection and try again.";

/**
 * - `loading`: until the game first arrives.
 * - `ready`: loaded. A refresh that fails keeps the last game.
 * - `not-found`: the game doesn't exist, or the player isn't in it.
 * - `load-error`: the first load failed, until `refresh` succeeds.
 */
export type BoardGameStatus = 'loading' | 'ready' | 'not-found' | 'load-error';

export interface BoardGameState {
  status: BoardGameStatus;
  game?: BoardGameView;
  /** Why the last move didn't go through, or that the game had moved on. */
  notice?: string;
  refresh: () => void;
  play: (placed: BoardGamePlacement[], swapped: number[]) => Promise<BoardGameMoveOutcome>;
  pass: (swapped: number[]) => Promise<BoardGameMoveOutcome>;
  swap: (tiles: number[]) => Promise<BoardGameMoveOutcome>;
}

export interface UseBoardGameOptions {
  pollMs?: number;
  visibility?: PageVisibility;
}

/** Whether the game can change without the player: only while it's active and the opponent is to move. */
const waitingOnOpponent = (game: BoardGameView | undefined): boolean => game?.status === 'active' && !game.isMyTurn;

/**
 * One board game, kept current by polling rather than subscriptions, as the games list is (the API scales to zero and
 * runs one instance): fetched when the screen opens, when the tab is shown or the window regains focus, and every
 * `pollMs` while the opponent is to move and the tab is visible. On the player's own turn, and once the game is over,
 * nothing changes it but the player, so nothing is polled.
 *
 * The moves send the turn number the player saw. The server's answer is what's shown: a written move's game, a
 * refusal's reason, and for a move that arrived after the turn had passed (a double submit, a retry after a lost
 * response, another tab) the game as it now stands. An expired session goes to sign-in.
 */
export const useBoardGame = (
  api: BoardGameApi,
  gameId: string,
  { pollMs = BOARD_GAME_POLL_MS, visibility = documentVisibility }: UseBoardGameOptions = {},
): BoardGameState => {
  const { goToSignIn } = useSession();
  const [game, setGame] = useState<BoardGameView>();
  const [status, setStatus] = useState<BoardGameStatus>('loading');
  const [notice, setNotice] = useState<string>();
  const gameRef = useRef<BoardGameView>(undefined);
  const mounted = useRef(true);

  /** Shows the game, unless it is older than the one shown: a poll that crossed a move's answer. */
  const show = useCallback((next: BoardGameView) => {
    const shown = gameRef.current;
    if (shown && next.turnNumber < shown.turnNumber) return;
    if (shown && next.turnNumber !== shown.turnNumber) setNotice(undefined);
    gameRef.current = next;
    setGame(next);
    setStatus('ready');
  }, []);

  const load = useCallback(async () => {
    try {
      const next = await api.game(gameId);
      if (mounted.current) show(next);
    } catch (error) {
      const failure = boardGameFailure(error);
      if (failure.kind === 'unauthenticated') return goToSignIn();
      if (!mounted.current) return;
      if (failure.kind === 'gameNotFound') return setStatus('not-found');
      logger.warn({ message: 'Loading the board game failed', error: (error as Error).message });
      if (!gameRef.current) setStatus('load-error');
    }
  }, [api, gameId, goToSignIn, show]);

  useEffect(() => {
    mounted.current = true;
    gameRef.current = undefined;
    setGame(undefined);
    setStatus('loading');
    void load();
    const whenVisible = () => {
      if (visibility.isVisible() && (gameRef.current === undefined || waitingOnOpponent(gameRef.current))) {
        void load();
      }
    };
    const unsubscribe = visibility.subscribe(whenVisible);
    const timer = setInterval(() => {
      if (visibility.isVisible() && waitingOnOpponent(gameRef.current)) void load();
    }, pollMs);

    return () => {
      mounted.current = false;
      unsubscribe();
      clearInterval(timer);
    };
  }, [load, pollMs, visibility]);

  const refresh = useCallback(() => {
    if (!gameRef.current) setStatus('loading');
    void load();
  }, [load]);

  const send = useCallback(
    async (move: (seenTurnNumber: number) => Promise<BoardGameMoveResult>): Promise<BoardGameMoveOutcome> => {
      const seen = gameRef.current;
      if (!seen) return 'failed';
      setNotice(undefined);
      try {
        const result = await move(seen.turnNumber);
        if (result.kind === 'refused') {
          setNotice(refusalMessage(result.refusal));
          return 'refused';
        }
        show(result.game);
        return 'accepted';
      } catch (error) {
        const failure = boardGameFailure(error);
        switch (failure.kind) {
          case 'unauthenticated':
            goToSignIn();
            return 'failed';
          case 'gameNotFound':
            setStatus('not-found');
            return 'failed';
          case 'staleTurn':
          case 'notYourTurn':
          case 'gameNotActive':
            await load();
            setNotice(MOVED_ON_MESSAGE);
            return 'refused';
          case 'moveMalformed':
            setNotice(MALFORMED_MESSAGE);
            return 'refused';
          default:
            logger.warn({ message: 'A board game move failed', error: (error as Error).message });
            setNotice(MOVE_FAILED_MESSAGE);
            return 'failed';
        }
      }
    },
    [goToSignIn, load, show],
  );

  const play = useCallback(
    (placed: BoardGamePlacement[], swapped: number[]) => send((seen) => api.playMove(gameId, seen, placed, swapped)),
    [api, gameId, send],
  );
  const pass = useCallback(
    (swapped: number[]) => send((seen) => api.passTurn(gameId, seen, swapped)),
    [api, gameId, send],
  );
  const swap = useCallback(
    (tiles: number[]) => send((seen) => api.swapTiles(gameId, seen, tiles)),
    [api, gameId, send],
  );

  return { status, game, notice, refresh, play, pass, swap };
};
